import {
  Button,
  Select,
  SelectChangeEvent,
  InputLabel,
  FormControl,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SessionStore } from '../services/SessionStore';
import { UrlHelper } from '../services';
import { useNavigate } from 'react-router';
import { AppDispatch, AppState } from '../store';
import { ESessionStore, EUrlParams } from '../data/Constants';
import { resetError } from '../store/states/ErrorSlice';
import { setRefreshTrue } from '../store/states/ConfigurationSlice';
import { TFunction } from 'i18next';
import type { IExtendedConfigureResponse } from '../../types';

/**
 * Sets the viewId to the first available view if present and updates session storage.
 * @param {string[]} viewIds - Array of available view ids
 * @param {(value: string) => void} setViewId - Setter for viewId state
 */
function handleViewParameter(viewIds: string[], setViewId: (value: string) => void) {
  if (viewIds?.length > 0) {
    setViewId(viewIds[0]);
    SessionStore.set(EUrlParams.ViewId, viewIds[0]);
  }
}

/**
 * Returns the invalid view message for the error dialog.
 * @param {string} view - The current view id
 * @param {TFunction} t - Translation function
 * @returns {string} The invalid view message
 */
function invalidViewMessage(view: string, t: TFunction) {
  return view?.length
    ? t('errorMessages.viewId.invalid', { view })
    : t('errorMessages.viewId.invalidNoView');
}

/**
 * Returns the valid view message for the error dialog.
 * @param {IExtendedConfigureResponse} configuration - The configuration object
 * @param {TFunction} t - Translation function
 * @returns {string} The valid view message
 */
function validViewMessage(configuration: IExtendedConfigureResponse, t: TFunction) {
  return configuration?.viewIds?.length === 1
    ? t('errorMessages.viewId.viewOk')
    : t('errorMessages.viewId.viewSelect');
}

interface ViewIdErrorProps {
  view: string;
  handleContact: (navigate: ReturnType<typeof useNavigate>, dispatch: AppDispatch) => void;
}

/**
 * Error dialog for invalid or missing viewId. Allows user to select a valid view and retry.
 * @param {ViewIdErrorProps} props - The component props
 * @returns {JSX.Element} The error dialog UI
 */
export const ViewIdError: React.FC<ViewIdErrorProps> = ({ view, handleContact }) => {
  const dispatch = useDispatch<AppDispatch>();
  const configuration = useSelector((state: AppState) => state.configuration);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const viewList: string[] = configuration.viewIds;
  const [viewId, setViewId] = useState('');

  /**
   * Handles view selection change from the dropdown.
   * @param {SelectChangeEvent<string | number>} event - The select change event
   */
  const onHandleView = (event: SelectChangeEvent<string | number>) => {
    SessionStore.set(ESessionStore.ViewId, event.target.value.toString());
    setViewId(event.target.value.toString());
  };

  // Set the initial viewId on mount
  useEffect(() => {
    handleViewParameter(configuration.viewIds, setViewId);
  }, []);

  /**
   * Loads the configuration for the selected view and resets error state.
   */
  const selectView = async () => {
    // Set  loadConfiguration to true for reloading configuration with new viewId
    dispatch(setRefreshTrue());
    const params = UrlHelper.deleteSearchParameter(ESessionStore.ViewId);
    navigate({ search: params }, { replace: true });
    SessionStore.set(ESessionStore.ViewId, SessionStore.get(ESessionStore.ViewId));
    dispatch(resetError());
  };

  // To display error dialog when viewId is empty array
  if (!configuration.viewIds || configuration.viewIds.length === 0) {
    return (
      <>
        <div>{t('errorMessages.viewId.noViews')}</div>
        <br />
        <Button className="close-dialog" onClick={() => handleContact(navigate, dispatch)}>
          <span>{t('button.contactUS')}</span>
        </Button>
        <Button onClick={() => dispatch(resetError())} className="btn-ok">
          <span>{t('button.ok')}</span>
        </Button>
      </>
    );
  }

  return (
    <>
      {configuration.viewIds?.length === 0 ? (
        <span>{t('errorMessages.viewId.noView')}</span>
      ) : (
        <>
          <span>
            {invalidViewMessage(view, t)}
            <br />
            {validViewMessage(configuration, t)}
          </span>

          {configuration.viewIds?.length > 0 && (
            <FormControl fullWidth className="viewError">
              <InputLabel>{t('userPreference.view')}</InputLabel>

              {configuration.viewIds.length > 1 && (
                <Select
                  color="primary"
                  label="View Id"
                  value={viewId ?? ''}
                  onChange={onHandleView}
                  inputProps={{ readOnly: configuration.viewIds.length === 1 }}
                >
                  {viewList.map((val) => (
                    <MenuItem value={val} key={val}>
                      {val.split('_')[1]}
                    </MenuItem>
                  ))}
                </Select>
              )}

              {configuration.viewIds.length === 1 && (
                <>
                  <Select
                    color="primary"
                    label="View Id"
                    value={SessionStore.get(ESessionStore.ViewId) ?? ''}
                    onChange={onHandleView}
                    inputProps={{ readOnly: true }}
                  >
                    {viewList.map((val) => (
                      <MenuItem value={val} key={val}>
                        {val.split('_')[1]}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{t('errorMessages.viewId.oneView')}</FormHelperText>
                </>
              )}
            </FormControl>
          )}
        </>
      )}

      <br />

      <Button className="close-dialog" onClick={() => handleContact(navigate, dispatch)}>
        <span>{t('button.contactUS')}</span>
      </Button>

      <Button onClick={selectView} className="btn-ok" data-testid="btn-ok">
        <span>{t('button.ok')}</span>
      </Button>
    </>
  );
};
